<template>
  <div class="form-wrapper">
    <form id="financing-form" class="financing-form" @submit.prevent="$emit('submit')">
      <form-select
        id="productType"
        class="product-type"
        data-qa="financing-product-type-dropdown"
        :model-value="financialParams.productType"
        :options="productTypes"
        :wrapper-class="'input'"
        :title="retrieveModalText('typedropdownlabel')"
        :is-read-only="isReadOnly"
        @change="productTypeChange"
      />

      <img
        :src="`/wwwroot/images/${dictionaryFolder}/lenen-kost-geld.jpg`"
        class="afm-banner"
        data-qa="financing-image"
        alt="Let op! Geld lenen kost geld"
      />

      <form-field
        id="vehiclePrice"
        :title="modalLabels.vehiclePrice.label"
        :class="modalLabels.vehiclePrice.body ? 'has-info' : ''"
      >
        <info-modal
          v-if="modalLabels.vehiclePrice.body"
          :title="modalLabels.vehiclePrice.label"
          :content="modalLabels.vehiclePrice.body"
          data-qa="financing-vehicle-price-info-icon"
        />
        <span class="input">
          <span class="euro-prefix"> € </span>
          <input
            id="vehiclePrice"
            data-qa="vehiclePrice"
            type="text"
            :value="i18n.n(Math.round(vehiclePrice))"
            disabled
          />
        </span>
        <span v-if="isFinancialLease && isVehiclePriceExcl" data-qa="vat-amount-label">
          {{ vatAmountLabel }}
        </span>
      </form-field>
      <price-input
        id="downPayment"
        v-model="financialParams.downPayment.value"
        name="downPayment"
        :title="modalLabels.downPayment.label"
        :class="modalLabels.downPayment.body ? 'has-info' : ''"
        :tab-on-enter="true"
        tabindex="1"
        :fraction-digits="0"
        :is-read-only="isReadOnly"
        @change="handleChangeParam($event, 'downPayment')"
      >
        <template #info>
          <info-modal
            v-if="modalLabels.downPayment.body"
            :title="modalLabels.downPayment.label"
            :content="modalLabels.downPayment.body"
            data-qa="financing-down-payment-info-icon"
          />
        </template>
        <div v-if="!isReadOnly">
          {{ i18n.n(financialParams.downPayment.min, 'currencyRounded') }} -
          {{ i18n.n(financialParams.downPayment.max, 'currencyRounded') }}
        </div>
      </price-input>
      <div
        v-if="exchangeCarValue > 0"
        data-qa="exchange-car-value"
        v-html="exchangeCarValueLabel"
      ></div>
      <price-input
        id="balloonPayment"
        v-model="financialParams.balloonPayment.value"
        :title="modalLabels.balloonPayment.label"
        :tab-on-enter="true"
        tabindex="1"
        :class="modalLabels.balloonPayment.body ? 'has-info' : ''"
        :fraction-digits="0"
        :is-read-only="isReadOnly"
        @change="handleChangeParam($event, 'balloonPayment')"
      >
        <template #info>
          <info-modal
            v-if="modalLabels.balloonPayment.body"
            :title="modalLabels.balloonPayment.label"
            :content="modalLabels.balloonPayment.body"
            data-qa="financing-balloon-payment-info-icon"
          />
        </template>
        <div v-if="!isReadOnly">
          {{ i18n.n(financialParams.balloonPayment.min, 'currencyRounded') }} -
          {{ i18n.n(financialParams.balloonPayment.max, 'currencyRounded') }}
        </div>
      </price-input>
      <form-field
        id="duration"
        class="duration"
        data-qa="duration"
        :title="modalLabels.duration.label"
        tabindex="1"
        :class="modalLabels.duration.body ? 'has-info' : ''"
      >
        <info-modal
          v-if="modalLabels.duration.body"
          :title="modalLabels.duration.label"
          :content="modalLabels.duration.body"
          data-qa="financing-duration-info-icon"
        />
        <span class="input">
          <input
            id="duration"
            type="number"
            data-qa="duration"
            :value="financialParams.duration.value"
            :min="financialParams.duration.min"
            :max="financialParams.duration.max"
            :step="financialParams.duration.step"
            :disabled="isReadOnly || null"
            @blur="handleChangeParam($event)"
            @keydown.enter.prevent="$event.target.blur()"
          />
        </span>
        <span>{{ retrieveModalText('durationpostfix') }}</span>
      </form-field>
      <form-field
        id="interestRate"
        class="interest-rate"
        data-qa="interest-rate"
        :title="modalLabels.interestRate.label"
        :class="modalLabels.interestRate.body ? 'has-info' : ''"
      >
        <info-modal
          v-if="modalLabels.interestRate.body"
          :title="modalLabels.interestRate.label"
          :content="modalLabels.interestRate.body"
          data-qa="financing-interest-rate-info-icon"
        />
        <span class="input">
          <span
            data-qa="financing-interest-rate"
            @mousedown.prevent="handleInterestMousedown"
            @mouseup.prevent="handleInterestMouseup"
            @touchstart.prevent="handleInterestMousedown"
            @touchend.prevent="handleInterestMouseup"
            >{{ i18n.n(financialParams.interestRate) }} %</span
          >
        </span>
      </form-field>
    </form>

    <dl class="financing-summary">
      <div class="monthly-fee">
        <dt>{{ activeProductTypeName }}</dt>
        <dd data-qa="financing-summary-rate-per-month">
          {{
            financialParams.errors ? '-' : i18n.n(financialParams.ratePerMonth, 'currencyRounded')
          }}
          {{ retrieveModalText('summaryratepostfix') }}
        </dd>
      </div>
    </dl>

    <div v-if="financialParams.errors" class="errors" data-qa="financing-errors">
      <div v-for="error in financialParams.errors" :key="error">
        {{ error }}
      </div>
    </div>
    <dl v-if="!isFinancialLease" data-qa="financing-details" class="financing-details">
      <div>
        <dt>
          {{ retrieveModalText('detailsnetcreditamount') }}
        </dt>
        <dd data-qa="financing-summary-details-net-credit-amount">
          {{
            financialParams.errors
              ? '-'
              : i18n.n(financialParams.netCreditAmount, 'currencyRounded')
          }}
        </dd>
      </div>
      <div>
        <dt>
          {{ retrieveModalText('detailsratepermonth') }}
        </dt>
        <dd data-qa="financing-summary-details-rate-per-month">
          {{
            financialParams.errors ? '-' : i18n.n(financialParams.ratePerMonth, 'currencyRounded')
          }}
        </dd>
      </div>
      <div>
        <dt>
          {{ retrieveModalText('detailsgrosscreditamount') }}
        </dt>
        <dd data-qa="financing-summary-details-gross-credit-amount">
          {{
            financialParams.errors
              ? '-'
              : i18n.n(financialParams.grossCreditAmount, 'currencyRounded')
          }}
        </dd>
      </div>
      <div>
        <dt>
          {{ retrieveModalText('detailsinterestrate') }}
        </dt>
        <dd data-qa="financing-summary-details-interest-rate">
          {{ financialParams.errors ? '-' : `${i18n.n(financialParams.interestRate)} %` }}
        </dd>
      </div>
      <div>
        <dt>
          {{ retrieveModalText('detailseffectiveannualinterestrate') }}
        </dt>
        <dd data-qa="financing-summary-details-effective-annual-interest-rate">
          {{
            financialParams.errors
              ? '-'
              : `${i18n.n(financialParams.effectiveAnnualInterestRate)}  %`
          }}
        </dd>
      </div>
      <div>
        <dt>
          {{ retrieveModalText('detailsduration') }}
        </dt>
        <dd>
          {{ financialParams.errors ? '-' : financialParams.duration.value }}
        </dd>
      </div>
    </dl>

    <p
      class="disclaimer"
      data-qa="financing-summary-details-disclaimer"
      v-html="retrieveInfoText('disclaimer')"
    ></p>
    <div class="button-container">
      <app-button type="submit" form="financing-form" data-qa="financing-modal-submit">
        {{ retrieveModalText('confirmbutton') }}
      </app-button>
      <app-button
        v-if="vwpfsSilvaEnabled"
        type="button"
        data-qa="financing-modal-request-silva"
        :disabled="isReadOnly || isLoading || !!financialParams.errors"
        @click="requestSilva"
      >
        {{ retrieveModalText('silvaprompt/requestsilvabutton') }}
      </app-button>
    </div>

    <div v-if="isLoading" class="overlay">
      <loader />
    </div>
  </div>
</template>

<script>
import FormField from '@/components/generic/presentational/atoms/formField/FormField.vue'
import FormSelect from '@/components/generic/presentational/atoms/formSelect/FormSelect.vue'
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import Loader from '@/components/generic/presentational/atoms/loader/Loader.vue'
import PriceInput from '@/components/generic/presentational/atoms/priceInput/PriceInput.vue'
import InfoModal from '@/components/generic/presentational/atoms/infoModal/InfoModal.vue'
import FinancialServiceForm from '@/mixins/financialServices/financialServiceForm'

export default {
  components: {
    FormField,
    FormSelect,
    AppButton,
    Loader,
    PriceInput,
    InfoModal,
  },
  mixins: [FinancialServiceForm],
  inject: ['retrieveInfoText', 'channels', 'financingProductType'],
  props: {
    productTypes: {
      type: Array,
      required: true,
    },
    objectType: {
      type: String,
      required: true,
    },
    dictionaryFolder: {
      type: String,
      required: true,
    },
    vwpfsSilvaEnabled: {
      type: Boolean,
      required: true,
    },
    bpmCode: {
      type: String,
      required: true,
    },
  },
  emits: ['submit', 'updateFinancing', 'requestSilva'],
  data() {
    return {
      toggleChannelTimer: null,
      infoText: {
        vehiclePrice: {
          body: '',
          show: false,
        },
        downPayment: {
          body: '',
          show: false,
        },
        balloonPayment: {
          body: '',
          show: false,
        },
        duration: {
          body: '',
          show: false,
        },
        interestRate: {
          body: '',
          show: false,
        },
      },
      errors: null,
    }
  },
  computed: {
    modalLabels() {
      return {
        vehiclePrice: {
          body: this.retrieveInfoText('vehiclepriceinfotext'),
          label: this.retrieveInfoText('vehiclepricelabel'),
        },
        downPayment: {
          body: this.retrieveInfoText('downpaymentinfotext'),
          label: this.retrieveInfoText('downpaymentlabel'),
        },
        balloonPayment: {
          body: this.retrieveInfoText('balloonPaymentinfotext'),
          label: this.retrieveInfoText('balloonPaymentlabel'),
        },
        duration: {
          body: this.retrieveInfoText('durationinfotext'),
          label: this.retrieveInfoText('durationlabel'),
        },
        interestRate: {
          body: this.retrieveInfoText('interestRateinfotext'),
          label: this.retrieveInfoText('interestRatelabel'),
        },
      }
    },
    isFinancialLease() {
      return this.financialParams.productType === this.financingProductType.financialLease
    },
    vehiclePrice() {
      if (this.isFinancialLease) {
        if (!this.isVehiclePriceExcl) {
          return this.financialParams.netCreditAmount
        }
        return this.financialParams.vehiclePriceExcl
      }
      return this.financialParams.vehiclePriceIncl
    },
    isVehiclePriceExcl() {
      if (!this.isFinancialLease) return false

      switch (this.bpmCode) {
        case 'BF':
        case 'BE':
        case 'CF':
        case 'CC':
          return false
      }

      return true
    },
    activeProductTypeName() {
      return this.productTypes.find((type) => type.value === this.financialParams.productType)?.name
    },
    vatAmountLabel() {
      return this.i18n.t('/financialservices/financing/modal/financiallease/vatamountlabel', {
        vatAmount: this.i18n.n(this.financialParams.vatAmount, 'currencyRounded'),
      })
    },
    exchangeCarValue() {
      const exchangeCars = this.quote?.QuoteDetails?.exchangeCars?.filter(
        (car) => car?.Type != 'UNKNOWN'
      )

      let exchangeCarsPriceExcl = 0
      let exchangeCarsPriceIncl = 0

      if (Array.isArray(exchangeCars)) {
        exchangeCarsPriceExcl = exchangeCars
          .filter((car) => car?.Price?.Excl)
          .map((car) => parseFloat(car.Price.Excl))
          .reduce((sum, price) => sum + price, 0)

        exchangeCarsPriceIncl = exchangeCars
          .filter((car) => car?.Price?.incl)
          .map((car) => parseFloat(car.Price.incl))
          .reduce((sum, price) => sum + price, 0)
      }

      const exchangeValue = this.isFinancialLease ? exchangeCarsPriceExcl : exchangeCarsPriceIncl
      return exchangeValue ? parseFloat(exchangeValue) : 0
    },
    exchangeCarValueLabel() {
      return this.retrieveInfoText('exchangecarvaluelabel', {
        exchangeCarValue: this.i18n.n(this.exchangeCarValue, 'currencyRounded'),
      })
    },
  },
  methods: {
    productTypeChange(event) {
      this.$emit('updateFinancing', 'productType', event)
    },
    toggleChannel() {
      this.$emit(
        'updateFinancing',
        'channelIndex',
        this.financialParams.channelIndex < this.channels.length - 1
          ? this.financialParams.channelIndex + 1
          : 0
      )
    },
    handleInterestMousedown() {
      if (!this.isReadOnly) {
        this.toggleChannelTimer = setTimeout(this.toggleChannel, 1000)
      }
    },
    handleInterestMouseup() {
      if (!this.isReadOnly) {
        clearTimeout(this.toggleChannelTimer)
      }
    },
    requestSilva() {
      this.$emit('requestSilva')
    },
  },
}
</script>

<style lang="scss" scoped>
.form-wrapper {
  position: relative;
}

.afm-banner {
  margin: 1rem 0;
  height: 50px;
}

.button-container {
  display: flex;
  justify-content: space-between;
}

:deep(.form-item) {
  display: flex;
  align-items: center;
  margin: 0.5em 0;

  & label {
    width: 12rem;
    display: inline-block;
  }

  &.product-type .label {
    flex-basis: 13rem;
  }

  & .input {
    display: flex;
    flex-basis: 8rem;
    margin-right: 1rem;
  }

  & input[type='text'] {
    width: 6rem;
  }

  & .euro-prefix {
    display: inline-block;
    font-size: 1rem;
    line-height: 1.15;
    background: $gray-lighter;
    padding: calc(0.41667rem + 2px) 0.3rem;
    margin-left: 1rem;
  }

  &.has-info {
    & label {
      width: 10rem;
    }

    & .info {
      flex-basis: 2rem;
    }

    & .input {
      margin-left: 0;
    }
  }

  &.product-type {
    & .input {
      flex-basis: 22rem;
      margin-right: 0;
    }
  }

  &.duration {
    & input {
      flex-basis: 7rem;
      margin-left: 1rem;
    }
  }

  &.interest-rate {
    & .input {
      margin-left: 1rem;
    }
  }
}

.financing-summary {
  padding-top: 1rem;
  border-top: 1px solid $gray;
  margin-bottom: 1rem;

  & div {
    display: flex;
    justify-content: space-between;
  }

  & .monthly-fee {
    font-weight: bold;
  }
}

.financing-details {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 1rem;
  padding: 1rem 1rem 0;
  background: $gray-lighter;

  & div {
    flex-basis: calc(33% - 1rem);
    margin: 0 1rem 1rem 0;
  }

  & dd {
    font-weight: bold;
  }
}
</style>
